import { bookingBlockedMeta } from "./errorReporting"
import { trpc, trpcRetry } from "@carrot/bff-client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"

// Derived from the client so the consent identifiers can't drift from the server
// contract — passing an unknown consent fails to compile. `.mutate`'s input is typed
// `Input | void`, so exclude the void before indexing.
type SignConsentInput = Exclude<Parameters<typeof trpc["telemedicine"]["signConsent"]["mutate"]>[0], void>
// What the member did on the consent screen(s): one entry per shown consent with whether they signed it.
export type ShownConsents = SignConsentInput["shown"]
// A single consent + whether the member satisfies it and whether it gates Continue (from getConsentStatus).
export type ConsentRequirement = Awaited<ReturnType<typeof trpc["telemedicine"]["getConsentStatus"]["query"]>>[number]
export type ConsentType = ConsentRequirement["type"]

const CONSENT_STATUS_QUERY_KEY = "telemedicine.getConsentStatus"

// The member's per-consent requirements — the consent-gate decision. Its own query so signConsent
// can invalidate just this.
export function useConsentStatus() {
  return useQuery({
    queryKey: [CONSENT_STATUS_QUERY_KEY],
    queryFn: () => trpc.telemedicine.getConsentStatus.query(),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("consent", "failed to load consent requirements")
  })
}

export function useSignConsent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (shown: ShownConsents) => trpc.telemedicine.signConsent.mutate({ shown }),
    // Invalidate the consent query so a remount of the flow reflects the just-signed state instead of
    // gating on the pre-sign value.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CONSENT_STATUS_QUERY_KEY] }),
    meta: bookingBlockedMeta("consent", "failed to save consent")
  })
}

// ---- Booking-flow screening (OPS-9280) ----

// Types derived from the client so they can't drift from the server contract.
type GetScreeningQuestionsInput = Exclude<
  Parameters<typeof trpc["telemedicine"]["getScreeningQuestions"]["query"]>[0],
  void
>
export type ScreeningProgram = GetScreeningQuestionsInput["program"]
export type ScreeningAppointmentType = GetScreeningQuestionsInput["appointmentType"]

type SubmitScreeningInput = Exclude<Parameters<typeof trpc["telemedicine"]["submitScreening"]["mutate"]>[0], void>

const SCREENING_QUESTIONS_QUERY_KEY = "telemedicine.getScreeningQuestions"

export function useScreeningQuestions(program: ScreeningProgram, appointmentType: ScreeningAppointmentType) {
  return useQuery({
    queryKey: [SCREENING_QUESTIONS_QUERY_KEY, program, appointmentType],
    queryFn: () => trpc.telemedicine.getScreeningQuestions.query({ program, appointmentType }),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("screening", "failed to load screening questions")
  })
}

export function useSubmitScreening() {
  return useMutation({
    mutationFn: (input: SubmitScreeningInput) => trpc.telemedicine.submitScreening.mutate(input),
    meta: bookingBlockedMeta("screening", "failed to submit screening answers")
  })
}

// ---- Booking-flow entry gate (MEM-760 + MEM-780) ----

export type BookingEligibility = Awaited<ReturnType<typeof trpc["telemedicine"]["beginBooking"]["mutate"]>>

// Fires the one-shot `beginBooking` mutation on flow entry and exposes a query-like { data, isLoading }
// so BookingFlow's mount gate is unchanged. It's a mutation (not a query) because it can persist a
// known-data refer-out (MEM-780); re-fires when the program changes, and a repeat for the same program
// is idempotent server-side (write-on-change).
export function useBeginBooking(program: ScreeningProgram) {
  const mutation = useMutation({
    mutationFn: () => trpc.telemedicine.beginBooking.mutate({ program }),
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("eligibility", "failed to start booking (eligibility check)")
  })
  const { mutate } = mutation
  useEffect(() => {
    mutate()
  }, [mutate, program])
  // Loading until the one-shot mutation settles — covers the pre-fire idle tick and the pending state.
  return { data: mutation.data, isLoading: !mutation.isSuccess && !mutation.isError }
}

// ---- Booking-flow visit preferences (MEM-713) ----

// Types derived from the client so they can't drift from the server contract.
export type BookingOffering = Awaited<ReturnType<typeof trpc["telemedicine"]["getBookingOffering"]["query"]>>
export type Offering = BookingOffering["offerings"][number]

const BOOKING_OFFERING_QUERY_KEY = "telemedicine.getBookingOffering"

export function useBookingOffering(program: ScreeningProgram) {
  return useQuery({
    queryKey: [BOOKING_OFFERING_QUERY_KEY, program],
    queryFn: () => trpc.telemedicine.getBookingOffering.query({ program }),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("offering", "failed to load booking options")
  })
}

// ---- Booking-flow availability (MEM-716) ----

// Types derived from the client so they can't drift from the server contract. A slot
// mirrors the scheduling-api `Slot` wire model (UTC times + slotToken + host ids).
type GetAvailabilityInput = Exclude<Parameters<typeof trpc["telemedicine"]["getAvailability"]["query"]>[0], void>
// `outcome` reaches the UI unused for now: only `slots_available` may render times, and every other
// outcome shares one empty state. Distinguishing them for the member is designed, not built.
export type AvailabilityResult = Awaited<ReturnType<typeof trpc["telemedicine"]["getAvailability"]["query"]>>
export type AvailabilitySlot = AvailabilityResult["slots"][number]

const AVAILABILITY_QUERY_KEY = "telemedicine.getAvailability"

// Bookable slots for a visit type + location over a date window. Disabled until the caller
// has both a visit type and a location (the availability query's required inputs).
export function useAvailability(input: GetAvailabilityInput, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [
      AVAILABILITY_QUERY_KEY,
      input.program,
      input.offeringId,
      input.stateCode,
      input.language,
      input.from,
      input.to
    ],
    queryFn: () => trpc.telemedicine.getAvailability.query(input),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    // Availability changes slowly relative to a booking session; avoid refetching the full
    // window on every remount (e.g. toggling preferences back and forth).
    staleTime: 60_000,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("availability", "failed to load appointment times")
  })
}

// ---- Booking-flow booking (MEM-719) ----

// Types derived from the client so they can't drift from the server contract.
type BookAppointmentInput = Exclude<Parameters<typeof trpc["telemedicine"]["bookAppointment"]["mutate"]>[0], void>
// The booked appointment the confirmation screen renders (assigned provider + UTC visit window).
export type BookedAppointment = Awaited<ReturnType<typeof trpc["telemedicine"]["bookAppointment"]["mutate"]>>

// Redeems the chosen slot and creates the appointment. Not retried: booking is a write, and a
// failure keeps the member on the time picker to retry deliberately.
export function useBookAppointment() {
  return useMutation({
    mutationFn: (input: BookAppointmentInput) => trpc.telemedicine.bookAppointment.mutate(input),
    meta: bookingBlockedMeta("booking", "failed to book the appointment")
  })
}

// ---- Telemedicine access / dispatch (MEM-677 + MEM-761) ----

// The composite access decision (from the BFF contract): benefit eligibility (`eligible` +
// `programs`) fused with per-program qualification, plus a derived `entry` union the entry points
// dispatch on. Supersedes the old eligibility-only getTelemedicineEntry.
export type TelemedicineAccess = Awaited<ReturnType<typeof trpc["telemedicine"]["getTelemedicineAccess"]["query"]>>

const TELEMEDICINE_ACCESS_QUERY_KEY = "telemedicine.getTelemedicineAccess"

export function useTelemedicineAccess(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [TELEMEDICINE_ACCESS_QUERY_KEY],
    queryFn: () => trpc.telemedicine.getTelemedicineAccess.query(),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("access", "failed to load telemedicine access")
  })
}
